// oxlint-disable no-unused-vars
import { ImageRun, patchDocument, PatchType, TextRun, Table, TableRow, TableCell, Paragraph, AlignmentType, WidthType } from "docx";
import { AreaUnit, JPBReportData, YearGrouping } from "./JPBState";
import { formatDate, calculateWeightedMeanForSeries, formatPricePerSquareMeter, formatCurrency, formatArea, formatPercentage, calculateEffectiveMarkSquareMeterPrice } from "./JPBFunc";
import { Localization } from "@viamap/viamap2-common";

async function getImageSizeFromBytes(bytes: Uint8Array): Promise<{ width: number; height: number } | undefined> {
   return new Promise((resolve) => {
      const blob = new Blob([bytes as any], { type: "image/png" });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
         const width = img.naturalWidth;
         const height = img.naturalHeight;
         URL.revokeObjectURL(url);
         resolve({ width, height });
      };
      img.onerror = () => {
         URL.revokeObjectURL(url);
         resolve(undefined);
      };
      img.src = url;
   });
}

export async function handleGenerateJPBRapport(data: JPBReportData) {
   // MARK: GET VARIABLES
   const ANTAL_AAR = String(data.yearGrouping);
   const ANTAL = String(data.referenceProperties.length || 0);
   const ANTAL_FOKUSERET = String(data.checkedDocumentIds.length || 0);
   const GENNEMSNIT_KR_M_IKKE_REGULERET = formatPricePerSquareMeter(calculateWeightedMeanForSeries(data.referenceProperties, 'original') ?? 0, AreaUnit.SquareMeter, undefined, false)
   const GENNEMSNIT_KR_HA_IKKE_REGULERET = formatPricePerSquareMeter(calculateWeightedMeanForSeries(data.referenceProperties, 'original') ?? 0, AreaUnit.Hectare, undefined, false)
   const GENNEMSNIT_KR_M_REGULERET = formatPricePerSquareMeter(calculateWeightedMeanForSeries(data.referenceProperties, 'indexed') ?? 0, AreaUnit.SquareMeter, undefined, false)
   const GENNEMSNIT_KR_HA_REGULERET = formatPricePerSquareMeter(calculateWeightedMeanForSeries(data.referenceProperties, 'indexed') ?? 0, AreaUnit.Hectare, undefined, false)
   const MAANED_AAR_FRA = getMaanedAar(new Date(new Date().getFullYear() - Number(ANTAL_AAR), 0, 1));
   const MAANED_AAR_TIL = getMaanedAar(new Date(new Date().getFullYear(), 11, 31));
   const KVARTAL_AAR = getKvartalAar();

   // Compute image ratio for overview image (if present)
   let overviewTransformation: { width: number; height: number } | undefined;
   if (data.overviewImage) {
      const size = await getImageSizeFromBytes(data.overviewImage);
      if (size && size.width && size.height) {
         const targetWidth = 600;
         const ratio = size.height / size.width;
         overviewTransformation = {
            width: targetWidth,
            height: Math.round(targetWidth * ratio)
         };
      }
   }

   // Compute image ratio for heatmap image (if present)
   let heatmapTransformation: { width: number; height: number } | undefined;
   if (data.heatmapImage) {
      const size = await getImageSizeFromBytes(data.heatmapImage);
      if (size && size.width && size.height) {
         const targetWidth = 600;
         const ratio = size.height / size.width;
         heatmapTransformation = {
            width: targetWidth,
            height: Math.round(targetWidth * ratio)
         };
      }
   }

   // Compute image ratio for jordbund image (if present)
   let jordbundTransformation: { width: number; height: number } | undefined;
   if (data.overviewWithJordbundImage) {
      const size = await getImageSizeFromBytes(data.overviewWithJordbundImage);
      if (size && size.width && size.height) {
         const targetWidth = 600;
         const ratio = size.height / size.width;
         jordbundTransformation = {
            width: targetWidth,
            height: Math.round(targetWidth * ratio)
         };
      }
   }

   // Compute image ratio for histogram image (if present)
   let histogramTransformation: { width: number; height: number } | undefined;
   if (data.histogramImage) {
      const size = await getImageSizeFromBytes(data.histogramImage);
      if (size && size.width && size.height) {
         const targetWidth = 600;
         const ratio = size.height / size.width;
         histogramTransformation = {
            width: targetWidth,
            height: Math.round(targetWidth * ratio)
         };
      }
   }

   // MARK: RETURN AND PATCH
   const templateUrl = new URL(
      "/demoDataJPB/20251112_Skabelon_Jordpriser_Kommune.docx",
      import.meta.url
   ).href;

   const response = await fetch(templateUrl);
   if (!response.ok) {
      console.error("Failed to fetch JPB report template", response.status, response.statusText);
      return;
   }

   const arrayBuffer = await response.arrayBuffer();

   const rapportBlob = await patchDocument({
      outputType: "blob",
      data: arrayBuffer,
      keepOriginalStyles: true,
      patches: {
         // KOMMUNENAVN: { type: PatchType.PARAGRAPH, children: [new TextRun("Mock KOMMUNENAVN")] },
         // POSTNUMMER_BY: { type: PatchType.PARAGRAPH, children: [new TextRun("1234 Mockby")] },
         // ADRESSE: { type: PatchType.PARAGRAPH, children: [new TextRun("Mock ADRESSE 1")] },
         // LANDSDEL: { type: PatchType.PARAGRAPH, children: [new TextRun("Mock landsdel")] },

         // AFDELING: { type: PatchType.PARAGRAPH, children: [new TextRun("Mock AFDELING")] },
         // ATT_KONTAKTPERSON: { type: PatchType.PARAGRAPH, children: [new TextRun("Mock Kontaktperson")] },
         // JNR: { type: PatchType.PARAGRAPH, children: [new TextRun("JNR-0000")] },
         // REF1: { type: PatchType.PARAGRAPH, children: [new TextRun("Ref1-mock")] },
         // REF2: { type: PatchType.PARAGRAPH, children: [new TextRun("Ref2-mock")] },
         // MAIL: { type: PatchType.PARAGRAPH, children: [new TextRun("mail@example.com")] },
         // INTERVAL_SPAN: { type: PatchType.PARAGRAPH, children: [new TextRun("0 (mock)")] },
         // INTERVAL_START: { type: PatchType.PARAGRAPH, children: [new TextRun("0 (mock)")] },
         // INTERVAL_SLUT: { type: PatchType.PARAGRAPH, children: [new TextRun("0 (mock)")] },
         // AGRO_PRIS_M: { type: PatchType.PARAGRAPH, children: [new TextRun("0 kr/m² (Agro, mock)")] },
         // AGRO_PRIS_HA: { type: PatchType.PARAGRAPH, children: [new TextRun("0 kr/ha (Agro, mock)")] },
         // INDSÆT_SENESTE_AGROCURA_STATISTIK: { type: PatchType.PARAGRAPH, children: [new TextRun("[Mock Agrocura statistik]")] }

         KVARTAL_AAR: { type: PatchType.PARAGRAPH, children: [new TextRun(KVARTAL_AAR)] },
         MAANED_AAR_FRA: { type: PatchType.PARAGRAPH, children: [new TextRun(MAANED_AAR_FRA)] },
         MAANED_AAR_TIL: { type: PatchType.PARAGRAPH, children: [new TextRun(MAANED_AAR_TIL)] },
         ANTAL_AAR: { type: PatchType.PARAGRAPH, children: [new TextRun(ANTAL_AAR)] },
         ANTAL: { type: PatchType.PARAGRAPH, children: [new TextRun(ANTAL)] },
         ANTAL_FOKUSERET: { type: PatchType.PARAGRAPH, children: [new TextRun(ANTAL_FOKUSERET)] },
         GENNEMSNIT_KR_M_IKKE_REGULERET: { type: PatchType.PARAGRAPH, children: [new TextRun(GENNEMSNIT_KR_M_IKKE_REGULERET)] },
         GENEMSNIT_KR_HA_IKKE_REGULERET: { type: PatchType.PARAGRAPH, children: [new TextRun(GENNEMSNIT_KR_HA_IKKE_REGULERET)] },
         GENNEMSNIT_KR_M_REGULERET: { type: PatchType.PARAGRAPH, children: [new TextRun(GENNEMSNIT_KR_M_REGULERET)] },
         GENNEMSNIT_KR_HA_REGULERET: { type: PatchType.PARAGRAPH, children: [new TextRun(GENNEMSNIT_KR_HA_REGULERET)] },

         ...(data.overviewImage && overviewTransformation
            ? {
               INDSÆT_KORT_MED_ALLE_IDENTIFICEREDE_JORDHANDLER_INDENFOR_KOMUNNEGRÆNSEN: {
                  type: PatchType.PARAGRAPH,
                  children: [
                     new ImageRun({
                        type: "png",
                        data: data.overviewImage,
                        transformation: overviewTransformation
                     } as any)
                  ]
               }
            }
            : {}),
         ...(data.heatmapImage && heatmapTransformation
            ? {
               INDSÆT_HEATMAP_MED_KATERGORIER_AF_JORDPRISINTERVALLER: {
                  type: PatchType.PARAGRAPH,
                  children: [
                     new ImageRun({
                        type: "png",
                        data: data.heatmapImage,
                        transformation: heatmapTransformation
                     } as any)
                  ]
               }
            }
            : {}),
         ...(data.overviewWithJordbundImage && jordbundTransformation
            ? {
               INDSÆT_KORT_MED_JODHANDLER_SAMT_LAG_MED_JORDTYPE: {
                  type: PatchType.PARAGRAPH,
                  children: [
                     new ImageRun({
                        type: "png",
                        data: data.overviewWithJordbundImage,
                        transformation: jordbundTransformation
                     } as any)
                  ]
               }
            }
            : {}),
         ...(data.histogramImage && histogramTransformation
            ? {
               PRICE_HISTOGRAM: {
                  type: PatchType.PARAGRAPH,
                  children: [
                     new ImageRun({
                        type: "png",
                        data: data.histogramImage,
                        transformation: histogramTransformation
                     } as any)
                  ]
               }
            }
            : {}),
         ...(createCheckedPropertiesTable(data.referenceProperties, data.checkedDocumentIds)
            ? {
               INDSÆT_TABEL_KUN_MED_ANVENDTE_JORDHANDLER: {
                  type: PatchType.DOCUMENT,
                  children: [createCheckedPropertiesTable(data.referenceProperties, data.checkedDocumentIds)!]
               }
            }
            : {}),
      }
   });

   const docxUrl = URL.createObjectURL(rapportBlob as Blob);

   const a = document.createElement("a");
   a.href = docxUrl;
   a.download = "Jordprisanalyse Rapport.docx";
   a.click();
   URL.revokeObjectURL(docxUrl);
}


function getKvartalAar(): string {
   const date = new Date();
   const year = date.getFullYear();
   const month = date.getMonth();
   const quarter = Math.floor(month / 3) + 1;
   return `${quarter}. kvartal ${year}`;
}

function getMaanedAar(date: Date): string {
   const year = date.getFullYear();
   const month = date.getMonth();
   const danishMonths = [
      "Januar", "Februar", "Marts", "April", "Maj", "Juni",
      "Juli", "August", "September", "Oktober", "November", "December"
   ];
   return `${danishMonths[month]} ${year}`;
}

/**
 * Creates a Word table with checked properties, matching JPBTable's default columns
 */
function createCheckedPropertiesTable(
   referenceProperties: JPBReportData['referenceProperties'],
   checkedDocumentIds: string[]
): Table | null {
   // Filter to only checked properties
   const checkedProperties = referenceProperties.filter(prop =>
      checkedDocumentIds.includes(prop.documentID)
   );

   if (checkedProperties.length === 0) {
      return null;
   }

   // Use SquareMeter as default (can be made configurable if needed)
   const areaUnit = AreaUnit.SquareMeter;
   const isHectare = (areaUnit as string) === (AreaUnit.Hectare as string);

   // Create header row
   const headerRow = new TableRow({
      children: [
         new TableCell({
            children: [new Paragraph({
               children: [new TextRun({ text: Localization.getText("Transaction date"), bold: true })],
               alignment: AlignmentType.LEFT
            })],
            shading: { fill: "D3D3D3" }
         }),
         new TableCell({
            children: [new Paragraph({
               children: [new TextRun({ text: Localization.getText("M_Area") + (isHectare ? " (ha)" : " (m²)"), bold: true })],
               alignment: AlignmentType.RIGHT
            })],
            shading: { fill: "D3D3D3" }
         }),
         new TableCell({
            children: [new Paragraph({
               children: [new TextRun({ text: Localization.getText("Total price"), bold: true })],
               alignment: AlignmentType.RIGHT
            })],
            shading: { fill: "D3D3D3" }
         }),
         new TableCell({
            children: [new Paragraph({
               children: [new TextRun({ text: isHectare ? Localization.getText("Price per ha") : Localization.getText("Price per m²"), bold: true })],
               alignment: AlignmentType.RIGHT
            })],
            shading: { fill: "D3D3D3" }
         }),
         new TableCell({
            children: [new Paragraph({
               children: [new TextRun({ text: Localization.getText("Farmland overlap"), bold: true })],
               alignment: AlignmentType.RIGHT
            })],
            shading: { fill: "D3D3D3" }
         }),
         new TableCell({
            children: [new Paragraph({
               children: [new TextRun({ text: isHectare ? Localization.getText("Effective mark price per ha") : Localization.getText("Effective mark price per m²"), bold: true })],
               alignment: AlignmentType.RIGHT
            })],
            shading: { fill: "D3D3D3" }
         })
      ]
   });

   // Create data rows
   const dataRows = checkedProperties.map(prop => {
      const effectivePrice = calculateEffectiveMarkSquareMeterPrice(prop);

      return new TableRow({
         children: [
            new TableCell({
               children: [new Paragraph({
                  children: [new TextRun({ text: formatDate(prop.transactionDate) })],
                  alignment: AlignmentType.LEFT
               })]
            }),
            new TableCell({
               children: [new Paragraph({
                  children: [new TextRun({ text: formatArea(prop.area, areaUnit) })],
                  alignment: AlignmentType.RIGHT
               })]
            }),
            new TableCell({
               children: [new Paragraph({
                  children: [new TextRun({ text: formatCurrency(prop.totalPrice) })],
                  alignment: AlignmentType.RIGHT
               })]
            }),
            new TableCell({
               children: [new Paragraph({
                  children: [new TextRun({ text: formatPricePerSquareMeter(prop.pricePerSquareMeter, areaUnit) })],
                  alignment: AlignmentType.RIGHT
               })]
            }),
            new TableCell({
               children: [new Paragraph({
                  children: [new TextRun({ text: formatPercentage(prop.overlapArea?.markOverlap) })],
                  alignment: AlignmentType.RIGHT
               })]
            }),
            new TableCell({
               children: [new Paragraph({
                  children: [new TextRun({ text: effectivePrice !== null ? formatPricePerSquareMeter(effectivePrice, areaUnit) : "-" })],
                  alignment: AlignmentType.RIGHT
               })]
            })
         ]
      });
   });

   return new Table({
      rows: [headerRow, ...dataRows],
      width: {
         size: 100,
         type: WidthType.PERCENTAGE
      }
   });
}